import { FC, ReactNode, useContext, useEffect, useRef } from "react";
import { PopoutRootContext, popoutModel } from "../popout-root";

import styles from './styles.module.scss'
import { IconClose } from "shared/assets/icons";

type PopoutProps = {
    id: string
    fill: string
    screen?: 'full' | 'modal'
    edge?: number
    close?: boolean
    children: ReactNode
}
export const Popout: FC<PopoutProps> = ({
    id,
    fill,
    screen,
    edge,
    close,
    children
}) => {

    const activePopout = useContext(PopoutRootContext)
    const dialogRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLElement | null>(null)

    const _close = () => {
        if (close === false) return
        popoutModel.events.setPopout(null)
    }

    useEffect(() => {
        if (activePopout !== id) return undefined
        triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const dialog = dialogRef.current
        if (!dialog) return undefined
        const restoreBackground = makeBackgroundInert(dialog)

        const focusFirst = () => {
            const first = getFocusable(dialog)[0]
            ;(first || dialog).focus()
        }
        const frame = window.requestAnimationFrame(focusFirst)
        let focusFrame = 0
        const keepFocusInside = () => {
            window.cancelAnimationFrame(focusFrame)
            focusFrame = window.requestAnimationFrame(() => {
                if (!dialog.contains(document.activeElement)) focusFirst()
            })
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                if (close !== false) popoutModel.events.setPopout(null)
                return
            }
            if (event.key !== 'Tab') return
            const focusable = getFocusable(dialog)
            const first = focusable[0] || dialog
            const last = focusable[focusable.length - 1] || dialog
            const outside = !dialog.contains(document.activeElement) || document.activeElement === dialog
            if (outside || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
                event.preventDefault()
                ;(event.shiftKey ? last : first).focus()
            }
        }
        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('focusin', keepFocusInside)
        document.addEventListener('focusout', keepFocusInside)

        return () => {
            window.cancelAnimationFrame(frame)
            window.cancelAnimationFrame(focusFrame)
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('focusin', keepFocusInside)
            document.removeEventListener('focusout', keepFocusInside)
            restoreBackground()
            triggerRef.current?.focus()
        }
    }, [activePopout, id, close])

    if (activePopout !== id) return null

    return (
        <div className={styles.popout} style={{ backgroundColor: fill }}>
            <div className={screen === 'full' ? styles.__full : styles.__size} style={{ padding: `0 ${edge ?? 0}px` }}>
                <div
                    ref={dialogRef}
                    className={screen === 'full' ? styles.mainFull : styles.main}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Запись на курс"
                    tabIndex={-1}
                >
                    {close !== false ? (
                        <div className={styles.controls}>
                        <button type="button" className={styles.close} onClick={_close} aria-label="Закрыть">
                            <IconClose width={24} height={24} aria-hidden="true" />
                        </button>
                        </div>
                    ) : null}
                    {children}
                </div>
            </div>
        </div>
    )
}

const getFocusable = (container: HTMLElement) => Array.from(
    container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
).filter((element) => {
    const style = window.getComputedStyle(element)
    return element.tabIndex >= 0 &&
        !element.matches(':disabled') &&
        !element.hasAttribute('hidden') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
})

const makeBackgroundInert = (dialog: HTMLElement) => {
    const changed: Array<{ element: HTMLElement; ariaHidden: string | null; inert: boolean }> = []
    let current: HTMLElement | null = dialog

    while (current?.parentElement) {
        const parent: HTMLElement = current.parentElement
        Array.from(parent.children).forEach((sibling) => {
            if (!(sibling instanceof HTMLElement) || sibling.contains(dialog)) return
            const inertSibling = sibling as HTMLElement & { inert: boolean }
            changed.push({
                element: sibling,
                ariaHidden: sibling.getAttribute('aria-hidden'),
                inert: inertSibling.inert,
            })
            inertSibling.inert = true
            sibling.setAttribute('aria-hidden', 'true')
        })
        current = parent
    }

    return () => changed.forEach(({ element, ariaHidden, inert }) => {
        const inertElement = element as HTMLElement & { inert: boolean }
        inertElement.inert = inert
        if (ariaHidden === null) element.removeAttribute('aria-hidden')
        else element.setAttribute('aria-hidden', ariaHidden)
    })
}
